import { Tab, Tabs } from '@mui/material';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import { LinkItem } from './LinkItem';
import { hasTooltipContent, LinkTabLabel } from './LinkTabLabel';

interface Props {
  readonly links: readonly LinkItem[];
}

/**
 * Native `disabled` blocks pointer events, so MUI Tooltip cannot show on disabled tabs.
 * When both disabled and tooltip are set, we keep the tab focusable for hover but block navigation.
 */
function isPseudoDisabledTab(link: LinkItem): boolean {
  return Boolean(link.disabled && hasTooltipContent(link.tooltip));
}

export function NavLinksMUI(p: Props) {
  const location = useLocation();

  const activeTabIndex = p.links.findIndex((link) => {
    if (link.isActive) {
      return link.isActive(matchPath(link.to, location.pathname), location);
    }
    return link.exact ? location.pathname === link.to : location.pathname.startsWith(link.to);
  });

  return (
    <Tabs
      value={activeTabIndex >= 0 ? activeTabIndex : 0}
      onChange={() => {
        // noop
      }}
      aria-label="navigation tabs"
      sx={{
        mt: -0.5,
        '& .MuiTabs-indicator': {
          backgroundColor: 'primary.main',
        },
      }}
      variant="scrollable"
      scrollButtons="auto"
    >
      {p.links.map((link, index) => {
        const pseudoDisabled = isPseudoDisabledTab(link);
        const tabDisabled = Boolean(link.disabled && !pseudoDisabled);

        return (
          <Tab
            key={link.to}
            label={<LinkTabLabel link={link} />}
            component={NavLink}
            disabled={tabDisabled}
            to={link.to}
            value={index}
            aria-disabled={link.disabled || undefined}
            tabIndex={pseudoDisabled ? -1 : undefined}
            onClick={(e) => {
              if (link.disabled) {
                e.preventDefault();
              }
            }}
            onAuxClick={(e) => {
              if (link.disabled && e.button === 1) {
                e.preventDefault();
              }
            }}
            sx={{
              fontWeight: 500,
              textTransform: 'uppercase',
              minHeight: '48px',
              ...(pseudoDisabled && {
                color: 'text.disabled',
                cursor: 'not-allowed',
              }),
              '&:hover': {
                color: pseudoDisabled ? 'text.disabled' : 'text.primary',
              },
              '&.Mui-selected': {
                color: pseudoDisabled ? 'text.disabled' : 'primary.main',
              },
            }}
          />
        );
      })}
    </Tabs>
  );
}
